"use client";

import { useEffect } from "react";
import { useTheme } from "next-themes";

function isTelegramWebApp() {
  return Boolean(window.Telegram?.WebApp?.initData) || window.location.hash.includes("tgWebAppData");
}

function telegramIsDark(): boolean {
  const scheme = window.Telegram?.WebApp?.colorScheme;
  if (scheme === "dark" || scheme === "light") return scheme === "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function applyTelegramChrome(dark: boolean) {
  const webApp = window.Telegram?.WebApp;
  if (!webApp) return;
  const params = webApp.themeParams;
  const header = params?.secondary_bg_color ?? params?.header_bg_color ?? (dark ? "#1a2420" : "#f4f0e6");
  const background = params?.bg_color ?? (dark ? "#15201b" : "#f3efe4");
  webApp.setHeaderColor?.(header);
  webApp.setBackgroundColor?.(background);
}

function telegramInsets() {
  const tg = window.Telegram?.WebApp as
    | (NonNullable<typeof window.Telegram>["WebApp"] & {
        expand?: () => void;
        safeAreaInset?: { top?: number; bottom?: number; left?: number; right?: number };
        contentSafeAreaInset?: { top?: number; bottom?: number; left?: number; right?: number };
      })
    | undefined;
  const sides = ["left", "right", "top", "bottom"] as const;
  const result = { left: 0, right: 0, top: 0, bottom: 0 };
  for (const side of sides) {
    result[side] = Math.max(tg?.safeAreaInset?.[side] ?? 0, tg?.contentSafeAreaInset?.[side] ?? 0);
  }
  return { tg, ...result };
}

let telegramSdkBooted = false;

function bootTelegramSdk(onReady: () => void) {
  if (!isTelegramWebApp()) return;
  if (telegramSdkBooted) {
    onReady();
    return;
  }
  telegramSdkBooted = true;
  void import("@telegram-apps/sdk-react")
    .then((sdk) => {
      sdk.init();
      mountMiniApp(sdk.miniApp);
      void mountViewport(sdk.viewport).finally(onReady);
    })
    .catch(() => {
      telegramSdkBooted = false;
    });
}

function mountMiniApp(miniApp: typeof import("@telegram-apps/sdk-react").miniApp) {
  if (!miniApp.isMounted()) {
    if (miniApp.mountSync.isAvailable()) miniApp.mountSync();
    else if (miniApp.mount.isAvailable() && !miniApp.isMounting()) {
      void miniApp.mount().catch(() => undefined);
    }
  }
  if (miniApp.ready.isAvailable()) miniApp.ready();
}

function mountViewport(viewport: typeof import("@telegram-apps/sdk-react").viewport) {
  const expand = () => {
    if (viewport.expand.isAvailable()) viewport.expand();
  };
  if (viewport.isMounted()) {
    expand();
    return Promise.resolve();
  }
  if (!viewport.mount.isAvailable() || viewport.isMounting()) return Promise.resolve();
  return viewport.mount().then(expand).catch(() => undefined);
}

function syncAppHeight() {
  const { tg, left, right } = telegramInsets();
  tg?.expand?.();
  const width = Math.round(window.visualViewport?.width ?? window.innerWidth);
  const height = Math.round(window.visualViewport?.height ?? window.innerHeight);
  document.documentElement.style.setProperty("--app-height", `${height}px`);
  if (left > 0) document.documentElement.style.setProperty("--safe-left", `${left}px`);
  if (right > 0) document.documentElement.style.setProperty("--safe-right", `${right}px`);
  document.documentElement.classList.toggle("wide", width > height);
}

export function TelegramProvider({ children }: { children: React.ReactNode }) {
  const { setTheme } = useTheme();

  useEffect(() => {
    function syncTheme() {
      const dark = telegramIsDark();
      setTheme(dark ? "dark" : "light");
      applyTelegramChrome(dark);
    }

    function onOrientation() {
      syncAppHeight();
      window.setTimeout(syncAppHeight, 80);
      window.setTimeout(syncAppHeight, 280);
    }

    syncTheme();
    syncAppHeight();
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    media.addEventListener("change", syncTheme);
    window.visualViewport?.addEventListener("resize", syncAppHeight);
    window.addEventListener("resize", syncAppHeight);
    window.addEventListener("orientationchange", onOrientation);
    window.Telegram?.WebApp?.onEvent?.("themeChanged", syncTheme);
    window.Telegram?.WebApp?.onEvent?.("viewportChanged", syncAppHeight);
    window.Telegram?.WebApp?.onEvent?.("safeAreaChanged", syncAppHeight);
    window.Telegram?.WebApp?.onEvent?.("contentSafeAreaChanged", syncAppHeight);

    const tg = window.Telegram?.WebApp as
      | (NonNullable<typeof window.Telegram>["WebApp"] & {
          expand?: () => void;
          disableVerticalSwipes?: () => void;
        })
      | undefined;
    tg?.ready?.();
    tg?.expand?.();
    tg?.disableVerticalSwipes?.();
    syncAppHeight();

    bootTelegramSdk(() => {
      syncTheme();
      syncAppHeight();
    });

    return () => {
      media.removeEventListener("change", syncTheme);
      window.visualViewport?.removeEventListener("resize", syncAppHeight);
      window.removeEventListener("resize", syncAppHeight);
      window.removeEventListener("orientationchange", onOrientation);
      window.Telegram?.WebApp?.offEvent?.("themeChanged", syncTheme);
      window.Telegram?.WebApp?.offEvent?.("viewportChanged", syncAppHeight);
      window.Telegram?.WebApp?.offEvent?.("safeAreaChanged", syncAppHeight);
      window.Telegram?.WebApp?.offEvent?.("contentSafeAreaChanged", syncAppHeight);
    };
  }, [setTheme]);

  return children;
}
